import { Typography } from 'antd';
import {
  BARCODE_FORMAT,
  BARCODE_FORMAT_PRIORITY,
} from 'common/components/product-add/components/constants';

import {
  gtinToUpc12,
  gtinToUpc13,
  upc12ToGTIN14,
  upc12ToUpc13,
  upc13ToGtin,
  upc13ToUpc12,
  upceToGTIN14,
  upceToUPC12,
  validateGTIN,
  validateUPC12,
  validateUPC13,
  validateUPCE,
} from 'common/components/product-add/components/utils/validation';

import { sleep } from 'utils/delay';

export const extractValueFromBarcode = (barcode = []) => {
  if (!barcode || !barcode.length) return {};

  let result = {};

  for (const [key, value] of Object.entries(BARCODE_FORMAT)) {
    const barcodeValue = barcode.find(
      (item) => item?.barcodeFormat?.toLowerCase() === key?.toLowerCase()
    );

    if (barcodeValue) {
      result = {
        ...result,
        [value]: barcodeValue?.barcodeText,
      };
    }
  }

  return result;
};

export const sortBarcodesByFormat = (barcodes = []) => {
  if (!barcodes || !barcodes?.length) return null;

  // Separate barcodes into two groups
  const nonPriorityBarcodes = [];
  const priorityBarcodes = [];

  barcodes.forEach((barcode) => {
    if (BARCODE_FORMAT_PRIORITY[barcode.barcodeFormat] !== undefined) {
      priorityBarcodes.push(barcode);
    } else {
      nonPriorityBarcodes.push(barcode);
    }
  });

  // Sort only the priority barcodes
  const sortedPriorityBarcodes = priorityBarcodes.sort((a, b) => {
    const priorityA = BARCODE_FORMAT_PRIORITY[a.barcodeFormat];
    const priorityB = BARCODE_FORMAT_PRIORITY[b.barcodeFormat];
    return priorityA - priorityB;
  });

  // Combine: non-priority first (in original order), then priority (sorted)
  return [...nonPriorityBarcodes, ...sortedPriorityBarcodes];
};

/**
 * Get auto-population results based on barcode type and value
 * @param {string} type - 'gtin' | 'upc13' | 'upc12' | 'upc8'
 * @param {string} value - The barcode value
 * @param {object} currentValues - Current form values { gtin, upc13, upc12, upc8 }
 * @returns {object} Fields to populate { gtin?, upc13?, upc12? }
 */
export const getAutoPopulateFields = (type, value, currentValues) => {
  // Return empty object if value is undefined, null, or empty string
  if (!value) {
    return {};
  }

  const { gtin, upc13, upc12 } = currentValues;
  const result = {};

  if (type === 'gtin' && validateGTIN(value)?.isValid) {
    // GTIN starts with 0: drop 1 leading zero to create UPC-13
    if (!upc13 && value.startsWith('0')) {
      const populatedUpc13 = gtinToUpc13(value);
      if (populatedUpc13) {
        result.upc13 = populatedUpc13;
      }
    }
    // GTIN starts with 00: drop 2 leading zeroes to create UPC-12
    if (!upc12 && value.startsWith('00')) {
      const populatedUpc12 = gtinToUpc12(value);
      if (populatedUpc12) {
        result.upc12 = populatedUpc12;
      }
    }
  }

  if (type === 'upc13' && validateUPC13(value)?.isValid) {
    // UPC-13 starts with 0: drop 1 leading zero to create UPC-12
    if (!upc12 && value.startsWith('0')) {
      const populatedUpc12 = upc13ToUpc12(value);
      if (populatedUpc12) {
        result.upc12 = populatedUpc12;
      }
    }
    // Prepend 1 zero to create GTIN
    if (!gtin) {
      const populatedGtin = upc13ToGtin(value);
      if (populatedGtin) {
        result.gtin = populatedGtin;
      }
    }
  }

  if (type === 'upc12' && validateUPC12(value)?.isValid) {
    // Prepend 1 zero to create UPC-13
    if (!upc13) {
      const populatedUpc13 = upc12ToUpc13(value);
      if (populatedUpc13) {
        result.upc13 = populatedUpc13;
      }
    }
    // Prepend 2 zeroes to create GTIN
    if (!gtin) {
      const populatedGtin = upc12ToGTIN14(value);
      if (populatedGtin) {
        result.gtin = populatedGtin;
      }
    }
  }

  if (type === 'upc8' && validateUPCE(value)?.isValid) {
    const convertedUpc12 = upceToUPC12(value);

    // Convert UPC-8 to UPC-12
    if (!upc12 && convertedUpc12) {
      result.upc12 = convertedUpc12;
    }

    // Populate UPC-13 (from converted UPC-12, independent of upc12 field)
    if (!upc13 && convertedUpc12) {
      const populatedUpc13 = upc12ToUpc13(convertedUpc12);
      if (populatedUpc13) {
        result.upc13 = populatedUpc13;
      }
    }

    // Convert UPC-8 to GTIN
    if (!gtin) {
      const populatedGtin = upceToGTIN14(value);
      if (populatedGtin) {
        result.gtin = populatedGtin;
      }
    }
  }

  return result;
};
/**
 * Determine the source field for auto-population based on available values
 * Priority: gtin > upc13 > upc12 > upc8
 * @param {object} values - { gtin, upc13, upc12, upc8 }
 * @returns {{ type: string, value: string } | null}
 */
export const getAutoPopulateSource = (values) => {
  const { gtin, upc13, upc12, upc8 } = values;

  if (gtin && validateGTIN(gtin)?.isValid) {
    return { type: 'gtin', value: gtin };
  }
  if (upc13 && validateUPC13(upc13)?.isValid) {
    return { type: 'upc13', value: upc13 };
  }
  if (upc12 && validateUPC12(upc12)?.isValid) {
    return { type: 'upc12', value: upc12 };
  }
  if (upc8 && validateUPCE(upc8)?.isValid) {
    return { type: 'upc8', value: upc8 };
  }

  return null;
};

export const formatBarcodeFieldLabel = (fieldName) => {
  return fieldName.toUpperCase().replace('UPC', 'UPC-');
};

export const setBarcodeAutoPopulateWarnings = async (
  form,
  fieldsToPopulate,
  sourceType
) => {
  if (Object.keys(fieldsToPopulate).length === 0) return;

  await sleep();
  const sourceLabel = formatBarcodeFieldLabel(sourceType);
  const warningFields = Object.keys(fieldsToPopulate).map((fieldName) => ({
    name: [fieldName],
    warnings: [
      <Typography.Text style={{ color: '#faad14' }}>
        {`${formatBarcodeFieldLabel(
          fieldName
        )} was auto-populated by ${sourceLabel}. Please verify this value`}
      </Typography.Text>,
    ],
  }));
  form.setFields(warningFields);
};

export const autoPopulateBarcodeFields = async (form, options = {}) => {
  const { showWarnings = true } = options;

  const gtin = form.getFieldValue('gtin');
  const upc8 = form.getFieldValue('upc8');
  const upc12 = form.getFieldValue('upc12');
  const upc13 = form.getFieldValue('upc13');

  const currentValues = { gtin, upc13, upc12, upc8 };
  const source = getAutoPopulateSource(currentValues);

  if (!source) return { source: null, fieldsToPopulate: {} };

  const fieldsToPopulate = getAutoPopulateFields(
    source.type,
    source.value,
    currentValues
  );

  if (Object.keys(fieldsToPopulate).length > 0) {
    form.setFieldsValue(fieldsToPopulate);

    if (showWarnings) {
      await setBarcodeAutoPopulateWarnings(form, fieldsToPopulate, source.type);
    }
  }

  return { source, fieldsToPopulate };
};
